import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Cap } from '@artmozaik/shared';
import { ROLE_LABEL } from '@artmozaik/shared';
import { AYAR_BOLUMLERI, AYAR_DIZINI, type AyarBolumu, type AyarKaydi } from '../kilavuz/ayarDizini';
import { useAuth } from '../lib/auth';
import AltPanel, { type AltPanelSonuc } from './AltPanel';
import { eslesme } from './aramaYardimci';

const DONUS_ANAHTARI = 'ccd-ayar-donus';

/** Ayarlar'a girmeden önce bulunulan sayfa: panel kapanınca oraya dönülür. */
export const ayarDonus = {
  yaz(yol: string) { try { sessionStorage.setItem(DONUS_ANAHTARI, yol); } catch { /* özel pencere */ } },
  oku(): string { try { return sessionStorage.getItem(DONUS_ANAHTARI) || '/'; } catch { return '/'; } },
};

export function gorunenAyarBolumleri(can: (c: Cap) => boolean): AyarBolumu[] {
  return AYAR_BOLUMLERI.filter((b) => !b.cap || can(b.cap));
}

/** Ayarlar paneli menüde `settings.view` ile görünür (ui/menu.ts); bölümler ayrıca kendi yetkisiyle süzülür. */
export const ayarPaneliVar = (can: (c: Cap) => boolean) => can('settings.view');

/**
 * Kullanıcının görebileceği ayar kayıtları (bölümü de kendisi de yetkiye uymalı).
 * Paneli göremeyen rolde, ayarın durduğu asıl ekran varsa (`yedek`) kayıt oraya bağlanır; yoksa hiç çıkmaz.
 */
export function gorunenAyarKayitlari(can: (c: Cap) => boolean): AyarKaydi[] {
  const panel = ayarPaneliVar(can);
  const bolumler = new Set(gorunenAyarBolumleri(can).map((b) => b.id));
  const sonuc: AyarKaydi[] = [];
  for (const k of AYAR_DIZINI) {
    if (k.cap && !can(k.cap)) continue;
    if (!k.bolum) sonuc.push(k);
    else if (!bolumler.has(k.bolum)) continue;
    else if (panel) sonuc.push(k);
    else if (k.yedek) sonuc.push({ ...k, yol: k.yedek.yol, nerede: k.yedek.nerede, bolum: undefined });
  }
  return sonuc;
}

export const ayarAdresi = (k: AyarKaydi) => `${k.yol}${k.capa ? `#${k.capa}` : ''}`;

/** Yan panel yalnız geniş ekranda vardır; dar ekranda liste içerik alanında çizilir. */
export function useGenisEkran(): boolean {
  const sorgu = '(min-width: 901px)';
  const [genis, setGenis] = useState(() => window.matchMedia(sorgu).matches);
  useEffect(() => {
    const m = window.matchMedia(sorgu);
    const degis = () => setGenis(m.matches);
    m.addEventListener('change', degis);
    return () => m.removeEventListener('change', degis);
  }, []);
  return genis;
}

/** Ayarlar'ın ikinci düzey paneli: bölüm listesi + ayar dizininde arama. */
export default function AyarPaneli({ gomulu }: { gomulu?: boolean }) {
  const { can, me } = useAuth();
  const navigate = useNavigate();
  const ogeler = useMemo(() => gorunenAyarBolumleri(can).map((b) => ({ to: `/ayarlar/${b.id}`, ad: b.ad, ikon: b.ikon, aciklama: b.aciklama })), [can]);
  const kayitlar = useMemo(() => gorunenAyarKayitlari(can), [can]);
  const ara = useCallback((q: string): AltPanelSonuc[] => kayitlar
    .map((k, i) => ({ k, i, puan: eslesme(q, k.ad, [...k.anahtarKelimeler, k.nerede]) }))
    .filter((x) => x.puan >= 0).sort((a, b) => a.puan - b.puan || a.i - b.i).slice(0, 30)
    .map(({ k, i }) => ({ anahtar: `${i}`, ad: k.ad, nerede: k.nerede, to: ayarAdresi(k) })), [kayitlar]);
  return (
    <AltPanel baslik="Ayarlar" gomulu={gomulu}
              geri={() => navigate(ayarDonus.oku())} geriEtiket="Ayarları kapat, ana menüye dön"
              ogeler={ogeler} aramaYer="Ayarlarda ara" ara={ara}
              ust={<><b>Art Mozaik</b><span>Operasyon paneli</span></>}
              alt={me ? <><b>{me.name}</b><span>{ROLE_LABEL[me.role]}</span></> : undefined} />
  );
}
